import { generatePath } from 'react-router-dom';
import {
  AddCampaignCreativeRequest,
  ApiRoutes,
  CampaignApiResponse,
  CampaignListApiResponse,
  CampaignTargetingSuggestions,
  CreateCampaignRequest,
  CreativeType,
  DefaultSuccessCampaignResponse,
  UpdateCampaignRequest,
  UpdatePublishedCampaignRequest,
} from 'types';
import { ApiRequestParams, axiosInstance } from './axiosInstance';
import { getApiUrl } from './helpers';

export async function fetchCampaign(campaignId: number, { signal }: ApiRequestParams) {
  const path = generatePath(ApiRoutes.Campaign, { campaignId: String(campaignId) });
  const { data } = await axiosInstance.get<CampaignApiResponse>(getApiUrl({ path }), { signal });
  return data;
}

export async function fetchCampaignList() {
  const path = generatePath(ApiRoutes.Campaigns);
  const { data } = await axiosInstance.get<CampaignListApiResponse>(getApiUrl({ path }));
  return data;
}

export async function fetchAllCampaigns() {
  const path = generatePath(ApiRoutes.AllCampaigns);
  const { data } = await axiosInstance.get<CampaignListApiResponse>(getApiUrl({ path }));
  return data;
}

export async function createCampaign(body?: CreateCampaignRequest) {
  const { data } = await axiosInstance.post<DefaultSuccessCampaignResponse>(
    getApiUrl({ path: generatePath(ApiRoutes.Campaigns) }),
    body
  );
  return data;
}

export async function updateCampaign(
  campaignId: number,
  payload: UpdateCampaignRequest,
  { signal }: ApiRequestParams = {}
) {
  const path = generatePath(ApiRoutes.Campaign, { campaignId: String(campaignId) });
  const { data } = await axiosInstance.put<DefaultSuccessCampaignResponse>(getApiUrl({ path }), payload, {
    signal,
  });
  return data;
}

export async function publishCampaign(campaignId: number, adminforce: boolean = false) {
  const path = generatePath(ApiRoutes.PublishCampaign, { campaignId: String(campaignId) });
  const { data } = await axiosInstance.post<DefaultSuccessCampaignResponse>(
    getApiUrl({ path }),
    {},
    { params: { adminforce } }
  );
  return data;
}

export async function deleteCampaign(campaignId: number) {
  const path = generatePath(ApiRoutes.Campaign, { campaignId: String(campaignId) });
  const { data } = await axiosInstance.delete<DefaultSuccessCampaignResponse>(getApiUrl({ path }));
  return data;
}

export async function duplicateCampaign(campaignId: number) {
  const path = generatePath(ApiRoutes.DuplicateCampaign, { campaignId: String(campaignId) });
  const { data } = await axiosInstance.post<DefaultSuccessCampaignResponse>(getApiUrl({ path }));
  return data;
}

export async function fetchCreativeTypeList() {
  const path = generatePath(ApiRoutes.GetCreativeTypes);
  const { data } = await axiosInstance.get<{ creativeTypes: CreativeType[] }>(getApiUrl({ path }));
  return data;
}

export async function addCampaignCreative({ campaignId, websiteId, file, creativeTypeId }: AddCampaignCreativeRequest) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('websiteId', String(websiteId));
  formData.append('creativeTypeId', creativeTypeId);
  const { data } = await axiosInstance.post<DefaultSuccessCampaignResponse>(
    getApiUrl({ path: generatePath(ApiRoutes.AddCampaignCreative, { campaignId: String(campaignId) }) }),
    formData
  );
  return data;
}

export async function deleteCampaignCreative(campaignId: number, creativeId: number) {
  const path = generatePath(ApiRoutes.DeleteCreative, {
    campaignId: String(campaignId),
    creativeId: String(creativeId),
  });
  const { data } = await axiosInstance.delete<DefaultSuccessCampaignResponse>(getApiUrl({ path }));
  return data;
}

export async function getTargetingGeoAutocomplete(query: string) {
  const searchParams = new URLSearchParams();
  searchParams.append('query', query);
  const path = `${ApiRoutes.GetCampaignTargetingGeoAutocomplete}?${searchParams.toString()}`;
  const { data } = await axiosInstance.get(getApiUrl({ path }));

  return data.items;
}

export async function updatePublishedCampaign(campaignId: number, payload: UpdatePublishedCampaignRequest) {
  const path = generatePath(ApiRoutes.PublishedCampaign, { campaignId: String(campaignId) });
  const { data } = await axiosInstance.put<DefaultSuccessCampaignResponse>(getApiUrl({ path }), payload);
  return data;
}

export async function fetchTargetingSuggestions(campaignId: number) {
  const path = generatePath(ApiRoutes.CampaignTargetingSuggestions, { campaignId: String(campaignId) });
  const { data } = await axiosInstance.get<CampaignTargetingSuggestions>(getApiUrl({ path }));

  return data;
}

export async function generateTargetingSuggestions(campaignId: number, websiteUrl: string) {
  const path =
    generatePath(ApiRoutes.CampaignTargetingSuggestions, { campaignId: String(campaignId) }) +
    '?url=' +
    encodeURIComponent(websiteUrl);
  const { data } = await axiosInstance.post<DefaultSuccessCampaignResponse>(getApiUrl({ path }));

  return data;
}

export async function stopCampaign(campaignId: number) {
  const path = generatePath(ApiRoutes.StopCampaign, { campaignId: String(campaignId) });
  const { data } = await axiosInstance.put<DefaultSuccessCampaignResponse>(getApiUrl({ path }));
  return data;
}
